import { useEffect, useRef, useState } from "react";
import * as signalR from "@microsoft/signalr";
import { ensureFreshToken } from "../services/tokenRefresh";
import { getUserToken } from "../utils/Util";
import { IOrderStatusChanged } from "../models/IOrders";

const HUB_URL =
  process.env.REACT_APP_HUB_URL ??
  "https://service.mutfacikservices.com/hubs/order";

/** Bağlantı kopsa da vazgeçmeden denemeye devam: 0 sn, 2 sn, 5 sn, 10 sn, sonra 30 sn'de bir. */
const RETRY_DELAYS = [0, 2000, 5000, 10000, 30000];
const retryDelay = (attempt: number) => RETRY_DELAYS[Math.min(attempt, RETRY_DELAYS.length - 1)];

export type OrderHubStatus = "idle" | "connecting" | "connected" | "reconnecting" | "disconnected" | "denied";

interface UseOrderHubOptions {
  /** JWT token — yalnızca "oturum var mı" kontrolü için; bağlantı her seferinde güncel token'ı kendisi okur. */
  token: string | null;
  /** Hangi restoran grubuna girilecek */
  restaurantId: string | null;
  /** Backend'den yeni sipariş push'u geldiğinde */
  onNewOrder?: () => void;
  /** Siparişlerden biri başka duruma geçtiğinde (restoran ya da kurye ilerletmiş olabilir) */
  onOrderStatusChanged?: (change: IOrderStatusChanged) => void;
  /**
   * Bağlantı koptuktan sonra yeniden kurulduğunda. Kopukluk sırasında gelen siparişler için
   * push gelmez; listeyi bir kez tazelemek kaçanları yakalar.
   */
  onResync?: () => void;
}

/**
 * Restoran paneli için sipariş hub'ı (/hubs/order).
 *
 * Eskiden token URL'ye bir kez gömülüyordu ve otomatik yeniden bağlanma birkaç denemeden
 * sonra vazgeçiyordu: token süresi (60 dk) dolduktan sonraki ilk kopuşta (deploy, ağ,
 * bilgisayar uykusu) bağlantı 401 alıp sessizce ölüyor, panel açık görünürken yeni
 * sipariş bildirimi hiç gelmiyordu. Artık her (yeniden) bağlanmada güncel token kullanılır,
 * denemeler bitmez ve kapanan bağlantı yeniden kurulur.
 */
export function useOrderHub({ token, restaurantId, onNewOrder, onOrderStatusChanged, onResync }: UseOrderHubOptions): OrderHubStatus {
  const [status, setStatus] = useState<OrderHubStatus>("idle");

  // Callback'leri ref'te tut — her render'da yeni fonksiyon gelse de bağlantı yeniden kurulmaz
  const onNewOrderRef = useRef(onNewOrder);
  const onOrderStatusChangedRef = useRef(onOrderStatusChanged);
  const onResyncRef = useRef(onResync);
  useEffect(() => { onNewOrderRef.current = onNewOrder; }, [onNewOrder]);
  useEffect(() => { onOrderStatusChangedRef.current = onOrderStatusChanged; }, [onOrderStatusChanged]);
  useEffect(() => { onResyncRef.current = onResync; }, [onResync]);

  const loggedIn = !!token;

  useEffect(() => {
    if (!loggedIn || !restaurantId) { setStatus("idle"); return; }

    let disposed = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let hasConnectedOnce = false;
    let denied = false;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(HUB_URL, {
        // Her bağlanma denemesinde çağrılır: süresi dolmuşsa önce yeniler, sonra güncel token'ı verir
        accessTokenFactory: async () => {
          await ensureFreshToken();
          return getUserToken()?.token ?? "";
        },
      })
      .withAutomaticReconnect({ nextRetryDelayInMilliseconds: (ctx) => retryDelay(ctx.previousRetryCount) })
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connection.on("NewOrderReceived", () => {
      onNewOrderRef.current?.();
    });

    connection.on("OrderStatusChanged", (change: IOrderStatusChanged) => {
      onOrderStatusChangedRef.current?.(change);
    });

    /** Gruba katıl. Yetki reddi tekrar denemekle düzelmez; ayrı durum olarak bildirilir. */
    const joinGroup = async () => {
      try {
        await connection.invoke("JoinRestaurantGroup", restaurantId);
        denied = false;
        setStatus("connected");
        return true;
      } catch (err) {
        console.error("[SignalR] Restoran grubuna katılınamadı — yeni sipariş bildirimi gelmeyecek:", err);
        denied = true;
        setStatus("denied");
        return false;
      }
    };

    const start = async (attempt: number) => {
      if (disposed) return;
      setStatus(hasConnectedOnce ? "reconnecting" : "connecting");
      try {
        await connection.start();
        if (disposed) return;
        console.log("[SignalR] Bağlandı →", HUB_URL);
        const joined = await joinGroup();
        if (joined && hasConnectedOnce) onResyncRef.current?.();
        hasConnectedOnce = true;
      } catch (err) {
        if (disposed) return;
        console.warn(`[SignalR] Bağlanılamadı, ${retryDelay(attempt + 1) / 1000} sn sonra tekrar denenecek:`, err);
        setStatus("disconnected");
        retryTimer = setTimeout(() => start(attempt + 1), retryDelay(attempt + 1));
      }
    };

    connection.onreconnecting(() => {
      if (!disposed) setStatus("reconnecting");
    });

    // Yeniden bağlanınca tekrar gruba katıl ve kopuklukta kaçanları yakala
    connection.onreconnected(async () => {
      console.log("[SignalR] Yeniden bağlandı");
      if (await joinGroup()) onResyncRef.current?.();
    });

    // Otomatik yeniden bağlanma da başarısız olursa (ya da sunucu kapatırsa) baştan kur
    connection.onclose(() => {
      if (disposed) return;
      setStatus("disconnected");
      retryTimer = setTimeout(() => start(1), retryDelay(1));
    });

    // Sekme arka plandan dönünce bağlantı ölmüşse beklemeden yeniden dene
    const onVisible = () => {
      if (document.visibilityState !== "visible" || disposed) return;
      if (connection.state === signalR.HubConnectionState.Disconnected) {
        clearTimeout(retryTimer);
        start(0);
      } else if (connection.state === signalR.HubConnectionState.Connected && denied) {
        joinGroup();
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    start(0);

    return () => {
      disposed = true;
      clearTimeout(retryTimer);
      document.removeEventListener("visibilitychange", onVisible);
      connection.off("NewOrderReceived");
      connection.off("OrderStatusChanged");
      if (connection.state === signalR.HubConnectionState.Connected) {
        connection.invoke("LeaveRestaurantGroup", restaurantId).catch(() => {});
      }
      connection.stop();
    };
  }, [loggedIn, restaurantId]); // token yenilenince bağlantı yeniden kurulmaz; accessTokenFactory güncelini okur

  return status;
}
